import { useState } from 'react'
import { GapNote } from '../../ui/Gaps'
import { formatMoney } from '@dorixona/utils'
import { useUnsavedWarning } from '../../ui/useUnsavedWarning'
import { Icon } from '../../ui/Icon'
import { Failed, Loading, failureText } from '../../ui/Load'
import { useSession } from '../auth/session'
import { useSaveSettings, useSettings, type Settings } from './api'
import { FIELDS, TABS, changed, fieldsOf, normalize, type Field, type Tab } from './model'
import { PageTexts } from './PageTexts'
import { changedEdits, usePageTexts } from './pages'

/** Вкладки экрана: разделы настроек и тексты страниц — у них свой запрос. */
type ScreenTab = Tab | 'pages'

const SCREEN_TABS: [ScreenTab, string][] = [...TABS, ['pages', 'Тексты страниц']]

export interface SettingsScreenProps {
  onToast: (message: string) => void
}

function FieldRow({
  field,
  value,
  disabled,
  onChange,
}: {
  field: Field
  value: string
  disabled: boolean
  onChange: (next: string) => void
}) {
  const id = `s-${field.key.replace(/\./g, '-')}`
  const hintId = field.hint ? `${id}-hint` : undefined
  const hint = field.hint ? (
    <span className="ui-hint" id={hintId}>
      {field.hint}
    </span>
  ) : null

  if (field.kind !== 'int') {
    if (field.kind === 'text') {
      return (
        <div className="ui-field">
          <label className="ui-label" htmlFor={id}>
            {field.label}
          </label>
          <input
            id={id}
            className="ui-input"
            type="text"
            disabled={disabled}
            aria-describedby={hintId}
            value={value}
            onChange={(e) => {
              onChange(e.target.value)
            }}
          />
          {hint}
        </div>
      )
    }

    // «Да / нет» в форме — флажок: переключатель в админке сохраняет сразу,
    // а здесь значение ждёт общей кнопки «Сохранить».
    return (
      <div className="st-check">
        <label className="ui-check">
          <input
            id={id}
            type="checkbox"
            disabled={disabled}
            checked={value === 'true'}
            aria-describedby={hintId}
            onChange={(e) => {
              onChange(e.target.checked ? 'true' : 'false')
            }}
          />
          {field.label}
        </label>
        {hint}
      </div>
    )
  }

  return (
    <div className="ui-field">
      <label className="ui-label" htmlFor={id}>
        {field.label}
      </label>
      <input
        id={id}
        className="ui-input st-num"
        type="number"
        inputMode="numeric"
        min={field.min}
        max={field.max}
        disabled={disabled}
        aria-describedby={hintId}
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
      />
      {/* Сумма деньгами, с разрядами: нули в «100000» глазами не считают. */}
      {field.money && value ? (
        <span className="ui-hint st-money">{formatMoney(Number(value) || 0)}</span>
      ) : null}
      {hint}
    </div>
  )
}

/**
 * Настройки сети. Значения приходят строками одним словарём, проверяет их
 * сервер; отправляем только то, что действительно изменили — иначе одна
 * опечатка в чужом поле уронила бы весь запрос.
 *
 * Правки всех вкладок копятся в одном черновике и уходят одной кнопкой
 * «Сохранить» в липкой полосе; точка у вкладки показывает, где они остались.
 */
export function SettingsScreen({ onToast }: SettingsScreenProps) {
  const { can } = useSession()
  const [tab, setTab] = useState<ScreenTab>('loy')
  const settings = useSettings()
  const save = useSaveSettings()
  // null — правок нет, форма показывает то, что на сервере.
  const [edits, setEdits] = useState<Settings | null>(null)
  const pages = usePageTexts()
  const pagesDirty = changedEdits(pages.draft).length > 0

  // Черновик появляется только у загруженных настроек — они тогда уже есть.
  const patch = edits ? changed(settings.data as Settings, edits) : {}
  const dirty = Object.keys(patch).length

  // Уход со страницы с несохранёнными правками браузер переспросит.
  useUnsavedWarning(dirty > 0 || pagesDirty)

  const canUpdate = can('settings', 'u')

  const tabs = (
    <div className="ui-tabs ui-detail-tabs" role="tablist" aria-label="Разделы настроек">
      {SCREEN_TABS.map(([id, name]) => (
        <button
          key={id}
          type="button"
          role="tab"
          className="ui-tab"
          aria-selected={tab === id}
          onClick={() => {
            setTab(id)
          }}
        >
          {name}
          {/* Правки не пропадают при переходе на другую вкладку — точка
              напоминает, где они ждут сохранения. */}
          {(id === 'pages' ? pagesDirty : fieldsOf(id).some((field) => field.key in patch)) ? (
            <span className="ui-dirty-dot" aria-label="есть несохранённые изменения" />
          ) : null}
        </button>
      ))}
    </div>
  )

  // Тексты страниц приходят своим запросом и от настроек не зависят.
  if (tab === 'pages') {
    return (
      <div className="st-page">
        {tabs}
        <PageTexts state={pages} canUpdate={canUpdate} onToast={onToast} />
      </div>
    )
  }

  if (settings.isPending) {
    return (
      <div className="st-page">
        {tabs}
        <Loading what="настройки" />
      </div>
    )
  }
  if (settings.isError) {
    return (
      <div className="st-page">
        {tabs}
        <Failed
          what="настройки"
          error={settings.error}
          onRetry={() => {
            void settings.refetch()
          }}
        />
      </div>
    )
  }

  const saved = settings.data
  const draft = edits ?? saved

  /**
   * Сохраняем то, что действительно изменили, предварительно приведя числа к
   * границам. Приводим здесь, а не при вводе: иначе поле нельзя было бы
   * очистить, чтобы набрать число заново.
   */
  function saveAll() {
    const clean: Settings = { ...draft }
    // Трогаем только те поля, которые правил пользователь, — на любой вкладке:
    // пройтись по всем значит отправить и нетронутые и получить отказ из-за
    // чужой опечатки. Тронутое поле — это поле с набранным значением.
    FIELDS.forEach((field) => {
      const value = draft[field.key]
      if (value === undefined || value === saved[field.key]) return
      clean[field.key] = normalize(field, value)
    })
    setEdits(clean)
    save.mutate(changed(saved, clean), {
      onSuccess: () => {
        setEdits(null)
        onToast('Настройки сохранены')
      },
      onError: (error) => {
        onToast(failureText(error))
      },
    })
  }

  return (
    <div className="st-page">
      {tabs}

      <section className="ui-card" aria-labelledby="st-title">
        <div className="ui-card-head">
          <h3 id="st-title">
            {TABS.find(([id]) => id === tab)?.[1]}
            {canUpdate ? null : <span className="ui-tag">только просмотр</span>}
          </h3>
          {tab === 'loy' || tab === 'ord' ? (
            <span className="aside">
              <Icon name="i-info" />
              действуют на новые заказы
            </span>
          ) : null}
        </div>
        <div className="ui-card-body ui-form">
          {fieldsOf(tab).map((field) => (
            <FieldRow
              key={field.key}
              field={field}
              value={draft[field.key] ?? ''}
              disabled={!canUpdate}
              onChange={(next) => {
                setEdits({ ...draft, [field.key]: next })
              }}
            />
          ))}
          {tab === 'loy' ? (
            <GapNote what="База начисления и округление кэшбэка" where="GET /admin/settings">
              Базу начисления и округление настройкой не поменять: кэшбэк считается только с
              оплаченной деньгами части — это решение бизнеса, зашитое в системе.
            </GapNote>
          ) : null}
          {tab === 'gen' ? (
            <GapNote what="Срок хранения журнала и область видимости" where="GET /admin/settings">
              Срок хранения журнала действий и правило «сотрудник видит только свою аптеку» — это
              правила системы, а не переключатели.
            </GapNote>
          ) : null}
        </div>
      </section>

      {canUpdate ? (
        <div className="ui-savebar" data-dirty={dirty ? true : undefined}>
          <span className="state" role="status">
            {dirty ? `Не сохранено изменений: ${dirty}` : 'Все изменения сохранены'}
          </span>
          {dirty ? (
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              disabled={save.isPending}
              onClick={() => {
                setEdits(null)
              }}
            >
              Отменить
            </button>
          ) : null}
          <button
            type="button"
            className="ui-btn"
            disabled={!dirty || save.isPending}
            aria-busy={save.isPending}
            onClick={saveAll}
          >
            Сохранить
          </button>
        </div>
      ) : null}
    </div>
  )
}
